import { z } from 'zod'

const price = z.number().int().nonnegative()
export const krProductSchema = z.object({
  id: z.string().min(1),
  code: z.string().min(1),
  name: z.string(),
  countryCode: z.literal('KR'),
  currency: z.literal('KRW'),
  originPrice: price,
  salePrice: price,
  details: z
    .array(
      z.object({
        targetType: z.enum(['SUBSCRIBE', 'TUTOR_CONNECT']),
        targetId: z.string(),
        quantity: z.number().int().positive(),
        target: z.unknown().optional(),
      }),
    )
    .min(1)
    .refine(
      (details) => details.some((detail) => detail.targetType === 'SUBSCRIBE'),
      'KR lesson product requires a lesson component',
    ),
})
export const krAcquisitionSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('LEGACY'),
    reason: z.enum(['NOT_PROVISIONED', 'EXISTING_CONTRACT']),
    subscribeId: z.string(),
    product: z.null(),
    checkoutProtocol: z.null(),
  }),
  z.object({
    mode: z.literal('PRODUCT'),
    reason: z.null(),
    subscribeId: z.string(),
    product: krProductSchema,
    checkoutProtocol: z.literal('KR_PREPARED_ORDER_V1'),
  }),
])
export type KrAcquisition = z.infer<typeof krAcquisitionSchema>
export const krOrderShippingSchema = z.object({
  orderName: z.string().trim().min(1),
  orderPhone: z.string().trim().min(1),
  orderAddress: z.string().trim().min(1),
  orderZipCode: z.string().trim().min(1),
  address: z.string().trim().min(1).nullable().optional(),
  detailAddress: z.string().trim().min(1).nullable().optional(),
})
export type KrOrderShipping = z.infer<typeof krOrderShippingSchema>

export const krPaymentOrderSchema = z.object({
  orderId: z.string().min(1),
  issueId: z.string().min(1),
  status: z.enum([
    'PREPARED',
    'PROCESSING',
    'FULFILLMENT_PENDING',
    'PAID',
    'FAILED',
    'SUPERSEDED',
    'REFUND_PENDING',
    'REFUNDED',
  ]),
  expiresAt: z.string(),
  productId: z.string().min(1),
  product: krProductSchema,
  subscribeId: z.string().min(1),
  paymentAmount: price,
  renewalAmount: price,
  currency: z.literal('KRW'),
  paymentMethod: z.enum(['CARD', 'KAKAO', 'NAVER', 'TOSS']),
  couponId: z.string().nullable().optional(),
  trialLevel: z.enum(['B', 'C1', 'C2', 'D', 'SMART_TALK']).nullable().optional(),
  canExecute: z.boolean().optional(),
  canReplace: z.boolean().optional(),
  authorityMode: z.enum(['BILLING_SERVER_V1', 'MANUAL_CONFIRM_V2', 'DIRECT_SDK']).nullable().optional(),
  shipping: krOrderShippingSchema.nullable().optional(),
  channelKey: z.string().min(1).nullable().optional(),
})
export type KrPaymentOrder = z.infer<typeof krPaymentOrderSchema>

/** Only the backend can prove that an attempted checkout is safe to replace. */
export function isKrOrderEditable(order: Pick<KrPaymentOrder, 'status' | 'canReplace'>) {
  return order.status === 'PREPARED' || (order.status === 'PROCESSING' && order.canReplace === true)
}

/** Retired orders remain available for late-approval reconciliation, never for checkout execution. */
export function isKrOrderRetired(order: Pick<KrPaymentOrder, 'status'>) {
  return ['SUPERSEDED', 'REFUND_PENDING', 'REFUNDED'].includes(order.status)
}
export const prepareKrPaymentOrderRequestSchema = z.object({
  subscribeId: z.string().min(1),
  expectedProductId: z.string().min(1),
  expectedAmount: price,
  couponId: z.string().nullable(),
  paymentMethod: krPaymentOrderSchema.shape.paymentMethod,
  idempotencyKey: z.string().min(1),
  replacesOrderId: z.string().min(1).optional(),
  shipping: krOrderShippingSchema.optional(),
  expectedTotalLessonCount: z.number().int().min(1).max(200).optional(),
  expectedValidityMonths: z.number().int().min(1).max(12).optional(),
  trialLevel: krPaymentOrderSchema.shape.trialLevel.unwrap().unwrap().optional(),
})
export type PrepareKrPaymentOrderRequest = z.infer<typeof prepareKrPaymentOrderRequestSchema>
export type PrepareKrPayment = ((claim?: boolean) => Promise<KrPaymentOrder | null>) & {
  cancel?: (orderId?: string) => Promise<void>
}

export function assertKrOrderMatches(
  order: KrPaymentOrder,
  request: Omit<PrepareKrPaymentOrderRequest, 'idempotencyKey'>,
) {
  if (request.expectedTotalLessonCount !== undefined || request.expectedValidityMonths !== undefined) {
    const target = order.product.details.find(
      (detail) => detail.targetType === 'SUBSCRIBE' && detail.targetId === order.subscribeId,
    )?.target
    const terms = z
      .object({
        productKind: z.literal('N_COUNT'),
        totalLessonCount: z.number().int(),
        validityMonths: z.number().int(),
      })
      .safeParse(target)
    if (
      !terms.success ||
      terms.data.totalLessonCount !== request.expectedTotalLessonCount ||
      terms.data.validityMonths !== request.expectedValidityMonths
    ) {
      throw new Error('KR_PAYMENT_ORDER_MISMATCH')
    }
  }
  if (
    order.subscribeId !== request.subscribeId ||
    order.productId !== request.expectedProductId ||
    order.paymentAmount !== request.expectedAmount ||
    order.paymentMethod !== request.paymentMethod ||
    (order.couponId ?? null) !== request.couponId ||
    (order.trialLevel ?? undefined) !== request.trialLevel ||
    !sameKrOrderShipping(order.shipping, request.shipping)
  ) {
    throw new Error('KR_PAYMENT_ORDER_MISMATCH')
  }
}

export function krOrderResumePath(order: KrPaymentOrder) {
  const query = new URLSearchParams({ purchase_order_id: order.orderId, subscribe_id: order.subscribeId })
  const path =
    order.trialLevel === 'SMART_TALK'
      ? `/subscribes/trial/smart-talk/${encodeURIComponent(order.subscribeId)}`
      : order.trialLevel
        ? '/subscribes/trial'
        : `/subscribes/payment/${encodeURIComponent(order.subscribeId)}`
  return `${path}?${query}`
}

export function clearKrPaymentReturn(url: URL): URL {
  const cleanUrl = new URL(url)
  for (const key of [
    'purchase_order_id',
    'product_id',
    'complete',
    'imp_uid',
    'merchant_uid',
    'payment_id',
    'paymentId',
    'tx_id',
    'issue_id',
    'customer_uid',
    'billingKey',
    'imp_success',
    'error_msg',
    'code',
    'message',
    'pg_provider',
    'payment_type',
    'amount',
    'paymentToken',
    'payment_token',
    'txId',
  ]) {
    cleanUrl.searchParams.delete(key)
  }
  return cleanUrl
}

export function isKrProviderCancelled(
  params: { purchase_order_id?: string; code?: string; imp_success?: string } | null | undefined,
): boolean {
  return Boolean(
    params?.purchase_order_id &&
    (params.imp_success === 'false' ||
      ['PAYMENT_CANCELLED', 'USER_CANCEL', 'PAY_PROCESS_CANCELED'].includes(params.code ?? '')),
  )
}

export const krPaymentRecoverySchema = z.object({
  order: krPaymentOrderSchema,
  action: z.enum(['RESUME_DIRECT', 'RESUME_BILLING', 'RESUME_CONFIRM', 'WAIT', 'COMPLETE']),
  reason: z.string().nullable().optional(),
})
export type KrPaymentRecovery = z.infer<typeof krPaymentRecoverySchema>

export function sameKrOrderShipping(
  first: KrOrderShipping | null | undefined,
  second: KrOrderShipping | null | undefined,
) {
  if (!first || !second) {
    return !first && !second
  }
  return (['orderName', 'orderPhone', 'orderAddress', 'orderZipCode', 'address', 'detailAddress'] as const).every(
    (key) => (first[key]?.trim() ?? null) === (second[key]?.trim() ?? null),
  )
}

/** A fresh-key preparation rejects these conditions before reserving an order or contacting a provider. */
export function isKrPreparationPriceError(error: unknown): boolean {
  return z
    .object({
      status: z.number().int().min(400).max(499),
      code: z.enum(['INVALID_PRODUCT', 'INVALID_PAYMENT_AMOUNT', 'COUPON_NOT_FOUND']),
    })
    .safeParse(error).success
}
